import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Highlighter from 'react-highlight-words';

import { CSS_VAR } from '../../../../styles/designTokens';
import { useRouter } from '../../../../hooks/useRouter';
import { useCan } from '../../../../permissions';
import getNavItems, { NavItem } from './navItems';
import useExpandedState from './useExpandedState';
import SidebarMenuItem from './SidebarMenuItem';
import {
    classifyQuickSearch,
    quickSearchUrl,
    resolveQuickSearch
} from '../../../../routes/Reservations/TravelFiles/utils/quickSearch';
import travelFilesApi from '../../../../api/travelFiles/api';

type Props = {
    /** The rail is closed: show the section icons only, no labels, no search. */
    collapsed?: boolean;
    /** Reopen the rail — a collapsed icon does this before opening its section. */
    onExpand?: () => void;
};

const SidebarMenuList = ({ collapsed = false, onExpand }: Props) => {
    const { location, history } = useRouter();
    const navItems = useMemo(() => getNavItems(), []);
    const can = useCan();

    const hasPermission = useCallback(
        (permissionId?: string): boolean => {
            if (!permissionId) return true;
            return can(permissionId);
        },
        [can]
    );

    // Filter nav tree: remove inaccessible items and empty categories
    const filterNavItems = useCallback(
        (items: NavItem[]): NavItem[] => {
            return items.reduce((acc, item) => {
                if (item.content) {
                    const filteredChildren = filterNavItems(item.content);
                    // Hide category if all children were filtered out
                    if (filteredChildren.length > 0) {
                        acc.push({ ...item, content: filteredChildren });
                    }
                } else {
                    // Leaf: only include if user has permission (or no restriction).
                    // permissionIdAny means "any of these" (e.g. view OR edit); it takes
                    // precedence over the single permissionId when present.
                    const allowed = item.permissionIdAny?.length
                        ? can.any(item.permissionIdAny)
                        : !item.permissionId || hasPermission(item.permissionId);
                    if (allowed) {
                        acc.push(item);
                    }
                }
                return acc;
            }, [] as NavItem[]);
        },
        [hasPermission, can]
    );

    const filteredNavItems = useMemo(
        () => filterNavItems(navItems),
        [navItems, filterNavItems]
    );

    const { isExpanded, toggle, expandRoot } = useExpandedState(filteredNavItems);

    // Only the top-level sections carry an icon, which is exactly what the
    // collapsed rail can show.
    const railItems = useMemo(() => filteredNavItems.filter((item) => item.icon), [filteredNavItems]);

    // Which section the current page belongs to. Longest match wins: every
    // Operations path also starts with Administration's.
    const activeRootPath = useMemo(() => {
        const matches = railItems.filter(
            (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
        );
        return matches.sort((a, b) => b.path.length - a.path.length)[0]?.path ?? null;
    }, [railItems, location.pathname]);

    const handleRailClick = useCallback(
        (itemPath: string) => {
            expandRoot(itemPath);
            onExpand?.();
        },
        [expandRoot, onExpand]
    );
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState<{ path: string; label: string }[]>([]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        setSearchValue(event.target.value);

    // Enter searches travel files: the text opens the travel file list with it in
    // the matching filter — Number, Tour Code, Phone, National ID, Ticket, PNR or
    // Name (see classifyQuickSearch). Typing still filters the menu, and a menu
    // result still opens on click.
    const canSearchTravelFiles = can('travel_file:view');
    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' || !canSearchTravelFiles) return;
        const filter = classifyQuickSearch(searchValue);
        if (!filter) return;
        event.preventDefault();
        setSearchValue('');
        setSearchResults([]);
        resolveQuickSearch(filter, async (nationalId) => {
            const response: any = await travelFilesApi.query({ query: { nationalId } as any, page: 1, pageSize: 1 });
            return Number(response?.total ?? response?.data?.length ?? 0);
        }).then((resolved) => history.push(quickSearchUrl(resolved)));
    };

    // Collect all leaf items matching the search query
    const searchNavItems = useCallback(
        (items: NavItem[], results: { path: string; label: string }[] = []) => {
            items.forEach((item) => {
                if (item.content) {
                    searchNavItems(item.content, results);
                } else {
                    const value =
                        searchValue[searchValue.length - 1] === ' '
                            ? searchValue.slice(0, -1)
                            : searchValue;
                    if (item.label?.toLowerCase().includes(value.toLowerCase()) && !item.notDeveloped) {
                        results.push({ path: item.path, label: item.label });
                    }
                }
            });
            return results;
        },
        [searchValue]
    );

    // Deduplicate search results
    const removeDuplicates = (array: { path: string; label: string }[]) => {
        if (!array.length) return array;
        const uniqueElements = [array[0]];
        for (let i = 1; i < array.length; i++) {
            if (array[i].path !== array[i - 1].path) {
                uniqueElements.push(array[i]);
            }
        }
        return uniqueElements;
    };

    // Debounced search (200ms)
    useEffect(() => {
        if (!searchValue.length) {
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(() => {
            const results = searchNavItems(filteredNavItems);
            setSearchResults(removeDuplicates(results));
        }, 200);
        return () => clearTimeout(timer);
    }, [searchValue]);

    // Render search results
    const renderSearchResults = useCallback(() => {
        return searchResults.length ? (
            searchResults.map((result) => (
                <Grid key={result.path} sx={{ padding: '8px 0', overflowWrap: 'break-word' }}>
                    <Link onClick={() => history.push(result.path)} color="secondary" sx={{ cursor: 'pointer', lineHeight: 1.5, '&:hover': { color: 'primary.main' } }}>
                        <Highlighter
                            highlightClassName="sidebar-search-highlight"
                            searchWords={[searchValue]}
                            textToHighlight={result.label}
                        />
                    </Link>
                    <Typography variant="body2" color="primary">
                        <Highlighter
                            highlightClassName="sidebar-search-highlight"
                            searchWords={[searchValue]}
                            textToHighlight={result.path.slice(1)}
                        />
                    </Typography>
                </Grid>
            ))
        ) : (
            <Grid sx={{ padding: '8px 0', overflowWrap: 'break-word' }}>
                <Typography color="primary">No items found</Typography>
            </Grid>
        );
    }, [searchResults]);

    // Recursive nav item renderer
    const renderNavItems = (items: NavItem[], parentPath: string | null, depth: number) =>
        items.map((item, index) => (
            <SidebarMenuItem
                key={item.path + (item.label || index)}
                item={item}
                parentPath={parentPath}
                depth={depth}
                isFirst={index === 0}
                isExpanded={isExpanded}
                onToggle={toggle}
                currentPath={location.pathname}
            >
                {item.content && renderNavItems(item.content, item.path, depth + 1)}
            </SidebarMenuItem>
        ));

    // ── Collapsed rail ────────────────────────────────────────────────────
    // Closing the sidebar used to leave nothing behind. The five section icons
    // stay: the app keeps a visible spine, and a click reopens the rail on that
    // section rather than making the user open it and then find the section.
    if (collapsed) {
        return (
            <List
                component="nav"
                disablePadding
                sx={{
                    padding: '8px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px'
                }}
            >
                {railItems.map((item) => (
                    <Tooltip key={item.path} title={item.label || ''} placement="right">
                        <IconButton
                            onClick={() => handleRailClick(item.path)}
                            aria-label={item.label}
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 'var(--radius-sm, 6px)',
                                // Same rule as the expanded rail: the tint marks
                                // the current section, the glyph stays put.
                                backgroundColor:
                                    activeRootPath === item.path ? 'var(--nav-active-bg)' : 'transparent',
                                '&:hover': { backgroundColor: 'var(--nav-hover-bg)' }
                            }}
                        >
                            {item.icon && (
                                <item.icon
                                    sx={{
                                        fontSize: 19,
                                        // Undo the global MuiSvgIcon width: 0.7em,
                                        // which is set without a height and squashes
                                        // the glyph.
                                        width: '1em',
                                        color: 'var(--nav-section-text, #5c6674)'
                                    }}
                                />
                            )}
                        </IconButton>
                    </Tooltip>
                ))}
            </List>
        );
    }

    return (
        <>
            <TextField
                sx={{
                    // Edge to edge, so it follows the sidebar when its width is dragged.
                    padding: '12px',
                    marginTop: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                    // Light grey placeholder — MUI's default leaves it at the
                    // near-black text colour, which reads as typed input.
                    // The field itself stays white.
                    '& .MuiOutlinedInput-input::placeholder': {
                        color: CSS_VAR.inputPlaceholder,
                        opacity: 1
                    }
                }}
                value={searchValue}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                // No browser autofill: its dropdown of past entries (from any field
                // on the site) covered the menu results under the box.
                autoComplete="off"
                inputProps={{ 'aria-label': 'search' }}
                placeholder="Search…"
                variant="outlined"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'accent.main' }} />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <IconButton
                            style={{ visibility: searchValue ? 'visible' : 'hidden' }}
                            onClick={() => {
                                setSearchValue('');
                                setSearchResults([]);
                            }}
                            size="large">
                            <ClearIcon />
                        </IconButton>
                    )
                }}
            />
            {!searchValue.length ? (
                <List component="nav" disablePadding sx={{ padding: '4px 6px' }}>
                    {renderNavItems(filteredNavItems, null, 0)}
                </List>
            ) : (
                <Grid sx={{ padding: '0 8px 0 16px' }}>
                    {canSearchTravelFiles && searchValue.trim() && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 0.5 }}>
                            Press{' '}
                            <Box
                                component="kbd"
                                sx={{
                                    px: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '4px',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Enter ↵
                            </Box>{' '}
                            to search travel files
                        </Typography>
                    )}
                    {searchValue && renderSearchResults()}
                </Grid>
            )}
        </>
    );
};

export default SidebarMenuList;
